import { useVirtualizer } from '@tanstack/react-virtual';
import clsx from 'clsx';
import {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  type ReactElement,
  Children,
} from 'react';
import type {
  GroupBase,
  MenuListProps as ReactSelectMenuListProps,
} from 'react-select';

import { NoOptionsMessage } from './NoOptionsMessage.js';
import type { OptionProps } from './Option.js';
import {
  applyStoredSelectionSortingOptions,
  sortSelectedOptions,
} from './sort-selected-options-first.js';
import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import type { MenuListFunctionRefObject } from '../Select.js';
import {
  selectVirtualizationWrapperCSS,
  selectMenuListWrapperCSS,
} from '../styles/Select.sty.js';
import type { BaseGroup, BaseOption } from '../types.js';

type MenuListProps<
  OptionValue,
  isMulti extends boolean,
  Group extends GroupBase<OptionValue>,
> = ReactSelectMenuListProps<OptionValue, isMulti, Group>;

export function MenuList<
  OptionValue,
  isMulti extends boolean,
  Group extends BaseGroup<OptionValue>,
>(props: Readonly<MenuListProps<BaseOption<OptionValue>, isMulti, Group>>) {
  const { children, selectProps, innerProps } = props;

  const parentRef = useRef<HTMLDivElement | null>(null);
  // Get a setter on the parentRef to get notified when the element re-renders.
  const [notifyParentRefChange, setNotifyParentRefChange] =
    useState<HTMLDivElement | null>();

  const initialRender = useRef(true);
  const sortingMap = useRef(new WeakMap());

  let childrenArray = Children.toArray(children) as Array<
    ReactElement<OptionProps<BaseOption<OptionValue>, isMulti, Group>>
  >;

  const currentChildrenLength = useRef(childrenArray.length);

  // During the initial rendering, the sorting is created and positions saved in a WeakMap.
  // If only the NoOptionsMessage/empty state is shown we don't need any sorting
  if (
    selectProps.showSelectedOptionsFirst &&
    !(childrenArray.length === 1 && childrenArray[0].type === NoOptionsMessage)
  ) {
    if (initialRender.current === true) {
      [childrenArray, sortingMap.current] = sortSelectedOptions(childrenArray);
    } else if (currentChildrenLength.current !== childrenArray.length) {
      currentChildrenLength.current = childrenArray.length;
      [childrenArray, sortingMap.current] = sortSelectedOptions(childrenArray);
    }
    // Otherwise the data is sorted by the initial sorting parameters.
    else {
      const [newChildrenArray, newSortingMap] =
        applyStoredSelectionSortingOptions(childrenArray, sortingMap.current);
      childrenArray = newChildrenArray;
      if (newSortingMap) {
        sortingMap.current = newSortingMap;
      }
    }
  }

  initialRender.current = false;

  const optionVirtualizer = useVirtualizer({
    count: childrenArray.length,
    getScrollElement: () => parentRef.current,
    overscan: 5,
    estimateSize: () => 32,
  });

  const menulistref = useMergeRefs([
    props.innerRef,
    parentRef,
    setNotifyParentRefChange,
    selectProps.contentRef,
  ]);

  useImperativeHandle(
    selectProps.menuListFunctionRef,
    (): MenuListFunctionRefObject => ({
      scrollToBottom: () => {
        optionVirtualizer.scrollToIndex(childrenArray.length);
      },
    }),
  );

  // When the parentElement re-renders, we will need to remeasure the virtualizer in order to
  // keep the correct height.
  useEffect(() => {
    requestAnimationFrame(() => {
      optionVirtualizer.measure();
    });
    // Deliberately leave optionVirtualizer out of the update deps to prevent a maximum update depth exceeded issue.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifyParentRefChange, childrenArray.length]);

  return (
    <div
      {...innerProps}
      ref={menulistref}
      className={clsx(selectMenuListWrapperCSS, 'strato-selectV2-menulist')}
      style={{
        width:
          optionVirtualizer.scrollOffset !== null &&
          optionVirtualizer.scrollOffset > 0 &&
          selectProps.contentWidth === 'max-content'
            ? optionVirtualizer.scrollRect?.width
            : undefined,
      }}
    >
      <div
        style={{
          height: optionVirtualizer.getTotalSize(),
          display: 'grid',
          gridTemplateRows: optionVirtualizer.measurementsCache
            .map((el) => `${el.size}px`)
            .join(' '),
        }}
        key={`wrapper-${selectProps.options.length}`}
        className={clsx(selectVirtualizationWrapperCSS)}
      >
        {optionVirtualizer.getVirtualItems().map((virtualRow) => {
          const child = childrenArray?.[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              className="strato-selectV2-option"
              // data-index is required for the virtualizer to map the measure
              // element correctly to the measured index.
              data-index={virtualRow.index}
              style={{
                gridRow: virtualRow.index + 1,
                minWidth: 0,
                height: 'fit-content',
              }}
              ref={(el) => {
                optionVirtualizer.measureElement(el);
              }}
            >
              {child}
            </div>
          );
        })}
      </div>
    </div>
  );
}
